import React from 'react';
import { Box, useTheme } from '@mui/material';
import { keyframes } from '@mui/material/styles';

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px) translateX(0px);
  }
  25% {
    transform: translateY(-20px) translateX(10px);
  }
  50% {
    transform: translateY(-10px) translateX(-10px);
  }
  75% {
    transform: translateY(-15px) translateX(5px);
  }
`;

const rotateAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.1);
  }
`;

export const EnhancedBackground: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -1,
        pointerEvents: 'none',
        overflow: 'hidden',
        backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#0a1929' : '#f5f5f5',
      }}
    >
      {/* Gradient overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isDark
            ? 'radial-gradient(circle at 5% 20%, rgba(29, 56, 141, 0.06) 0%, transparent 40%), radial-gradient(circle at 95% 80%, rgba(74, 144, 226, 0.05) 0%, transparent 40%)'
            : 'radial-gradient(circle at 5% 20%, rgba(29, 56, 141, 0.04) 0%, transparent 40%), radial-gradient(circle at 95% 80%, rgba(74, 144, 226, 0.03) 0%, transparent 40%)',
        }}
      />

      {/* Floating solar panel shapes */}
      <Box
        sx={{
          position: 'absolute',
          top: '5%',
          left: '2%',
          width: '120px',
          height: '120px',
          border: isDark ? '1px solid rgba(74, 144, 226, 0.12)' : '1px solid rgba(29, 56, 141, 0.1)',
          borderRadius: '8px',
          transform: 'rotate(15deg)',
          animation: `${floatAnimation} 20s ease-in-out infinite`,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: '1px',
            background: isDark ? 'rgba(74, 144, 226, 0.1)' : 'rgba(29, 56, 141, 0.08)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            left: '50%',
            top: 0,
            bottom: 0,
            width: '1px',
            background: isDark ? 'rgba(74, 144, 226, 0.1)' : 'rgba(29, 56, 141, 0.08)',
          },
        }}
      />

      {/* Small solar panel */}
      <Box
        sx={{
          position: 'absolute',
          bottom: '5%',
          right: '3%',
          width: '100px',
          height: '100px',
          border: isDark ? '1px solid rgba(74, 144, 226, 0.11)' : '1px solid rgba(29, 56, 141, 0.09)',
          borderRadius: '6px',
          transform: 'rotate(-20deg)',
          animation: `${floatAnimation} 25s ease-in-out infinite reverse`,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: '1px',
            background: isDark ? 'rgba(74, 144, 226, 0.09)' : 'rgba(29, 56, 141, 0.07)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            left: '50%',
            top: 0,
            bottom: 0,
            width: '1px',
            background: isDark ? 'rgba(74, 144, 226, 0.09)' : 'rgba(29, 56, 141, 0.07)',
          },
        }}
      />

      {/* Energy circles (representing renewable energy flow) */}
      <Box
        sx={{
          position: 'absolute',
          top: '15%',
          right: '2%',
          width: '180px',
          height: '180px',
          borderRadius: '50%',
          border: isDark ? '1px solid rgba(255, 215, 0, 0.1)' : '1px solid rgba(255, 215, 0, 0.08)',
          animation: `${pulseGlow} 8s ease-in-out infinite`,
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          left: '3%',
          width: '140px',
          height: '140px',
          borderRadius: '50%',
          border: isDark ? '1px solid rgba(74, 144, 226, 0.11)' : '1px solid rgba(29, 56, 141, 0.09)',
          animation: `${pulseGlow} 10s ease-in-out infinite 2s`,
        }}
      />

      {/* Lightning bolt path (very subtle) */}
      <svg
        style={{
          position: 'absolute',
          top: '50%',
          right: '5%',
          width: '70px',
          height: '100px',
          opacity: isDark ? 0.08 : 0.06,
        }}
      >
        <path
          d="M 40 10 L 20 50 L 35 50 L 15 90 L 50 45 L 35 45 L 55 10 Z"
          fill="none"
          stroke={isDark ? '#4a90e2' : '#1d388d'}
          strokeWidth="1.5"
          style={{
            animation: `${pulseGlow} 6s ease-in-out infinite`,
          }}
        />
      </svg>

      {/* Rotating wind turbine representation */}
      <Box
        sx={{
          position: 'absolute',
          top: '30%',
          left: '5%',
          width: '2px',
          height: '80px',
          background: isDark
            ? 'linear-gradient(to bottom, rgba(74, 144, 226, 0.12), transparent)'
            : 'linear-gradient(to bottom, rgba(29, 56, 141, 0.1), transparent)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-20px',
            left: '-20px',
            width: '40px',
            height: '40px',
            border: isDark ? '1px solid rgba(74, 144, 226, 0.11)' : '1px solid rgba(29, 56, 141, 0.09)',
            borderRadius: '50%',
            borderTopColor: 'transparent',
            borderRightColor: 'transparent',
            animation: `${rotateAnimation} 10s linear infinite`,
          },
        }}
      />

      {/* Dot grid pattern overlay */}
      <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0 }}>
        <defs>
          <pattern id="enhanced-dots" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill={isDark ? 'rgba(74, 144, 226, 0.05)' : 'rgba(29, 56, 141, 0.04)'} />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#enhanced-dots)" />
      </svg>

      {/* Circuit traces */}
      <svg
        style={{
          position: 'absolute',
          bottom: '20%',
          right: '2%',
          width: '150px',
          height: '80px',
          opacity: isDark ? 0.08 : 0.06,
        }}
      >
        <path
          d="M 10 40 L 50 40 L 50 15 L 80 15 M 50 40 L 50 65 L 80 65 M 80 15 L 95 15 L 95 40 L 140 40"
          fill="none"
          stroke={isDark ? '#4a90e2' : '#1d388d'}
          strokeWidth="1.5"
        />
        <circle cx="50" cy="40" r="2.5" fill={isDark ? '#4a90e2' : '#1d388d'} />
        <circle cx="95" cy="40" r="2.5" fill={isDark ? '#4a90e2' : '#1d388d'} />
      </svg>
    </Box>
  );
};

